import React from 'react';
import { APP_CONFIG, FALLBACK_UPI_ICON, getAuthorizationAmountForPaymentMethod } from '@/constants/payment';

interface PaymentStatusUIProps {
  status: 'pending' | 'failure' | null;
  amount?: number;
  timer?: number;
  appName: 'PHONEPE' | 'GPAY' | 'PAYTM' | string;
  cancelPayment?: (shouldCancelCoupon: boolean) => Promise<void>;
  onNavigate: () => void;
  isPending?: boolean;
  onCancelled?: () => void;
}

export default function PaymentStatusUI({
  status,
  timer = 0,
  appName,
  cancelPayment,
  onNavigate,
  isPending = false,
  onCancelled,
}: PaymentStatusUIProps) {
  if (!status) return null;

  // Internal handler: cancels coupon if needed, then navigates
  const handleClose = async () => {
    if (cancelPayment && isPending) {
      await cancelPayment(true);
      onCancelled?.();
    }
    onNavigate();
  };

  // If appName contains @, it's a UPI ID, use default icon
  const isUpiId = appName.includes('@');
  const config = isUpiId
    ? { icon: FALLBACK_UPI_ICON, label: 'UPI' }
    : APP_CONFIG[appName] || { icon: FALLBACK_UPI_ICON, label: 'UPI' };

  const { icon, label } = config;

  // Payment Failed UI
  if (status === 'failure') {
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0">
        <div className="w-full max-w-md bg-midnight-blue rounded-t-3xl p-8 text-white shadow-lg flex flex-col items-center animate-in slide-in-from-bottom duration-300">
          <div className="w-20 h-20 rounded-full bg-deep-blue flex items-center justify-center mb-6 relative">
            <span className="text-button-red text-5xl">✕</span>
          </div>
          <h2 className="text-2xl font-bold text-button-red mb-2">Payment Failed</h2>
          <p className="text-center text-base text-text-slate mb-8">Please try again or try changing the payment method. Any money deducted will be refunded in 5-7 days.</p>
          <button
            className="w-full max-w-xs py-3 rounded-full bg-button-red hover:bg-red text-white font-bold text-lg mb-6"
            onClick={handleClose}
          >Close</button>
          <div className="text-xs text-secondary-grey text-center mt-2">If the problem persists, please contact our support team</div>
        </div>
      </div>
    );
  }

  // Pending/Waiting UI
  if (status === 'pending') {
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0">
        <div className="w-full max-w-md bg-midnight-blue rounded-t-3xl p-6 text-white shadow-lg flex flex-col items-center animate-in slide-in-from-bottom duration-300">
          <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-4 relative z-10 border-2 border-highlight-gold/30">
            <img src={icon} alt={label} className="w-10 h-10" />
            <div className="absolute inset-0 rounded-full border-2 border-highlight-gold/50 animate-ping"></div>
          </div>
          <div className="text-white text-3xl font-bold mb-1">₹ {getAuthorizationAmountForPaymentMethod(appName)}</div>
          <div className="text-text-mute text-sm mb-6">Authorization amount</div>
          <div className="text-text-mute text-sm mb-1">Complete payment within</div>
          <div className="text-highlight-gold text-2xl font-bold mb-8">{Math.floor(timer / 60)}:{String(timer % 60).padStart(2, '0')}</div>
          <h2 className="text-highlight-gold text-xl font-bold mb-2">Waiting for Payment...</h2>
          <p className="text-text-slate text-center text-sm mb-8 px-4">Just open your UPI app and approve the payment request</p>
          <div className="flex gap-2 mb-10">
            <div className="w-3 h-3 bg-highlight-gold rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="w-3 h-3 bg-highlight-gold rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="w-3 h-3 bg-highlight-gold rounded-full animate-bounce"></div>
          </div>
          <div className="flex flex-col gap-4 w-full max-w-xs mb-10">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-highlight-gold flex items-center justify-center text-xs font-bold shrink-0 text-black">1</div>
              <span className="text-text-mute text-sm">Open UPI app on your phone</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-highlight-gold/80 flex items-center justify-center text-xs font-bold shrink-0 text-black">2</div>
              <span className="text-text-mute text-sm">Check for payment notification</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-highlight-gold/60 flex items-center justify-center text-xs font-bold shrink-0 text-black">3</div>
              <span className="text-text-mute text-sm">Enter UPI PIN to complete payment</span>
            </div>
          </div>
          <button className="w-full py-3 rounded-lg bg-tertiary-grey hover:bg-dark-grey text-white font-medium" onClick={handleClose}>Cancel Payment</button>
        </div>
      </div>
    );
  }

  return null;
}
